import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_URL } from "../config";

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshSession = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/auth/session`, { withCredentials: true });
            setUser(response.data.user);
            return response.data.user;
        } catch {
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshSession();
    }, [refreshSession]);

    const login = useCallback(async (email, password) => {
        const response = await axios.post(`${API_URL}/auth/login`, { email, password }, { withCredentials: true });
        setUser(response.data.user);
        return response.data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API_URL}/auth/logout`, null, { withCredentials: true });
        } finally {
            setUser(null);
        }
    }, []);

    const updateProfile = useCallback(async (displayName) => {
        const response = await axios.put(`${API_URL}/auth/profile`, { displayName }, { withCredentials: true });
        setUser(response.data.user);
        return response.data.user;
    }, []);

    const changePassword = useCallback(async (currentPassword, newPassword) => {
        await axios.post(`${API_URL}/auth/password`, { currentPassword, newPassword }, { withCredentials: true });
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, logout, refreshSession, updateProfile, changePassword }),
        [user, loading, login, logout, refreshSession, updateProfile, changePassword]
    );
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within AuthProvider");
    return context;
}
